import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { http } from '@/api/http';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import SnippetsOutlined from '@ant-design/icons/SnippetsOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate(); 

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await http.get('/order-edit-requests/pending');

      // data.data es el array del paginador de Laravel
      const items = Array.isArray(data.data) ? data.data : [];

      const mapped = items.map((item) => ({
        id: item.id,
        status: item.status,
        order_id: item.order_id,
        // Título amigable
        title: `Orden ${item.order?.order_number ?? `#${item.order_id}`}`,
        // Mensaje sencillo usando el usuario
        message: `${item.user?.first_name ?? item.user?.name ?? 'Un colaborador'} solicitó permiso para editar esta orden.`,
        // Etiqueta de tiempo (simple por ahora)
        time_label: new Date(item.created_at).toLocaleString(),
        // Por si luego quieres más info cruda
        raw: item
      }));

      setNotifications(mapped);
      setBadgeCount(data.total ?? mapped.length);
    } catch (error) {
      console.error('Error loading notifications', error);
    } finally {
      setLoading(false);
    }
  };

  // Carga la data al montar y cada vez que se abre el popper
  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const handleApprove = async (notif) => {
    try {
      await http.post(`/order-edit-requests/${notif.id}/approve`);
      // Actualizamos estado en frontend
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, status: 'approved' } : n))
      );
      setBadgeCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error al aprobar solicitud', error);
    }
  };

  const handleReject = async (notif) => {
    try {
      await http.post(`/order-edit-requests/${notif.id}/reject`);
      // Actualizamos estado en frontend
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, status: 'rejected' } : n))
      );
      setBadgeCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error al rechazar solicitud', error);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={badgeCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 420,
                maxWidth: { xs: 420, md: 420 }
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notificaciones"
                  elevation={0}
                  border={false}
                  content={false}
                
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {loading && (
                      <ListItem>
                        <ListItemText primary={<Typography variant="body2">Cargando notificaciones...</Typography>} />
                      </ListItem>
                    )}

                    {!loading && notifications.length === 0 && (
                      <ListItem>
                        <ListItemText primary={<Typography variant="body2">No hay notificaciones pendientes.</Typography>} />
                      </ListItem>
                    )}

                    {!loading &&
                      notifications.map((notif) => (
                        <ListItem
                          key={notif.id}
                          component={ListItemButton}
                          divider
                          // mientras sean "pending" los marcamos visualmente como seleccionados
                          selected={notif.status === 'pending'}
                          secondaryAction={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Typography variant="caption" noWrap>
                                {notif.time_label || ''}
                              </Typography>

                              {notif.status === 'pending' && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Aprobar solicitud">
                                    <IconButton
                                      size="large"
                                      color="success"
                                      onClick={(e) => {
                                        e.stopPropagation(); // para que no dispare el onClick del ListItem
                                        handleApprove(notif);
                                      }}
                                    >
                                      <CheckOutlined style={{ fontSize: '1rem' }} />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Rechazar solicitud">
                                    <IconButton
                                      size="large"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReject(notif);
                                      }}
                                    >
                                      <CloseOutlined style={{ fontSize: '1rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          }
                          onClick={() => {
                            // Aquí podrías navegar a la orden:
                            // navigate(`/orders/${notif.order_id}`);
                            // o abrir un modal, etc.
                            console.log('Ir a orden', notif.order_id);
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                              <SnippetsOutlined />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="h6" noWrap>
                                {notif.title}
                              </Typography>
                            }
                            secondary={notif.message}
                          />
                        </ListItem>
                      ))}

                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }} onClick={() => {
                        setOpen(false);
                        navigate('/order-edit-requests');
                      }}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            Ver todas
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
